import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

/**
 * Accessibility prop helpers
 * ==========================
 *
 * Two helpers live in this file. They solve adjacent but distinct
 * problems — read the docstrings before reaching for one:
 *
 *   1. `getAccessibilityProps(...)` — **authoring side.** Call sites
 *      pass a small platform-neutral options bag and receive the
 *      props that should be spread onto a Pressable / Text / button.
 *      Returns RN-style props on native (`accessibilityLabel`,
 *      `accessibilityRole`, `accessibilityHint`) and ARIA props on
 *      web (`aria-label`, `role`, `title`). Use this anywhere you
 *      author a new accessible element.
 *
 *   2. `translateRnA11yProps(...)` — **component side.** Used inside
 *      `.web.tsx` Gluestack primitives that render raw `<div>` /
 *      `<span>` and otherwise spread `{...props}` straight to the
 *      DOM. Accepts whatever RN-style accessibility props came in
 *      from the caller and rewrites them to ARIA / DOM equivalents
 *      so React doesn't warn and screen readers actually see them.
 *      Native primitives (`<View>` / `<Text>`) don't need this — RN
 *      and React Native Web translate the props themselves.
 *
 * Rule of thumb: if you're *writing* a feature, you want #1. If
 * you're *building a primitive* whose web implementation is raw DOM,
 * you want #2.
 */

type A11yRole = 'button' | 'link' | 'progressbar' | 'article' | 'none';

/**
 * Returns accessibility props appropriate for the platform.
 * On web, React DOM does not recognize React Native props (accessibilityLabel,
 * accessibilityRole, accessible) and will warn; use ARIA attributes instead.
 *
 * See file-level docstring for the distinction from `translateRnA11yProps`.
 */
export function getAccessibilityProps(options: {
  label?: string;
  role?: A11yRole;
  hint?: string;
  accessible?: boolean;
}): Record<string, string | boolean | undefined> {
  if (isWeb) {
    const props: Record<string, string | boolean | undefined> = {};

    if (options.label) {
      props['aria-label'] = options.label;
    }

    if (options.role) {
      props.role = options.role;
    }

    if (options.hint) {
      props.title = options.hint;
    }

    return props;
  }

  const props: Record<string, string | boolean | undefined> = {};

  if (options.label) {
    props.accessibilityLabel = options.label;
  }

  // `article` is a web (ARIA) role with no React Native equivalent. Android
  // rejects unknown accessibilityRole values and crashes the view (Sentry
  // PRIMUM-APP-2P2, opening a chat with extracted-question cards), so native
  // gets no role for it, which is how a plain container is announced anyway.
  if (options.role && options.role !== 'article') {
    props.accessibilityRole = options.role;
  }

  if (options.hint) {
    props.accessibilityHint = options.hint;
  }

  if (options.accessible !== undefined) {
    props.accessible = options.accessible;
  }

  return props;
}

/**
 * Translate React Native accessibility props to their ARIA / DOM
 * equivalents. Used by web-only Gluestack primitives (Box, HStack,
 * VStack, Text, etc.) that render raw `<div>` / `<span>` and would
 * otherwise leak unknown camelCased attributes to the DOM —
 * producing React warnings and silently breaking screen reader
 * semantics because the lowercased attribute name (e.g.
 * `accessibilitylabel`) isn't recognized by assistive tech.
 *
 * Native primitives (`<View>`, `<Text>`) don't need this — RN and
 * React Native Web do the translation themselves when the component
 * goes through their `createElement`. This helper closes the gap for
 * the raw-DOM web variants.
 *
 * Translations:
 *   - `nativeID` → `id`
 *   - `accessibilityLabel` → `aria-label`
 *   - `accessibilityLabelledBy` → `aria-labelledby`
 *   - `accessibilityRole` → `role`
 *   - `accessibilityHint` → `title`
 *   - `accessibilityLiveRegion` → `aria-live` (`'none'` → `'off'`)
 *   - `accessibilityElementsHidden` (iOS) and `importantForAccessibility`
 *     (Android) → `aria-hidden`, since hiding a subtree cross-platform means
 *     setting both
 *   - `accessibilityViewIsModal` → `aria-modal`
 *   - `accessibilityState` → split into `aria-disabled`,
 *     `aria-selected`, `aria-checked`, `aria-expanded`, `aria-busy`
 *   - `accessible` is dropped (no DOM analogue; native focus order
 *     differs from web tab order anyway)
 *
 * Returns `{ rest, aria }` — the caller is expected to spread
 * `{...rest} {...aria}` (rest first, aria second) so that any explicit
 * `aria-*` / `id` / `role` / `title` on the caller's props wins over
 * the RN-derived translation.
 *
 * See file-level docstring for the distinction from
 * `getAccessibilityProps`.
 */
export interface RnAccessibilityProps {
  nativeID?: string;
  accessibilityLabel?: string;
  accessibilityLabelledBy?: string;
  accessibilityRole?: string;
  accessibilityHint?: string;
  accessibilityLiveRegion?: 'none' | 'polite' | 'assertive';
  accessibilityElementsHidden?: boolean;
  /**
   * Android's counterpart to `accessibilityElementsHidden`, which is iOS-only.
   * Hiding a subtree on both platforms means setting the pair, so both are
   * translated here; passing only the first left this one reaching the DOM and
   * warning.
   */
  importantForAccessibility?: 'auto' | 'yes' | 'no' | 'no-hide-descendants';
  accessibilityViewIsModal?: boolean;
  accessibilityState?: {
    disabled?: boolean;
    selected?: boolean;
    checked?: boolean | 'mixed';
    expanded?: boolean;
    busy?: boolean;
  };
  accessible?: boolean;
}

interface AriaProps {
  id?: string;
  role?: string;
  title?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-live'?: 'off' | 'polite' | 'assertive';
  'aria-hidden'?: boolean;
  'aria-modal'?: boolean;
  'aria-disabled'?: boolean;
  'aria-selected'?: boolean;
  'aria-checked'?: boolean | 'mixed';
  'aria-expanded'?: boolean;
  'aria-busy'?: boolean;
}

export function translateRnA11yProps<T extends RnAccessibilityProps & Record<string, unknown>>(
  props: T
): {
  rest: Omit<
    T,
    | 'nativeID'
    | 'accessibilityLabel'
    | 'accessibilityLabelledBy'
    | 'accessibilityRole'
    | 'accessibilityHint'
    | 'accessibilityLiveRegion'
    | 'accessibilityElementsHidden'
    | 'importantForAccessibility'
    | 'accessibilityViewIsModal'
    | 'accessibilityState'
    | 'accessible'
  >;
  aria: AriaProps;
} {
  const {
    nativeID,
    accessibilityLabel,
    accessibilityLabelledBy,
    accessibilityRole,
    accessibilityHint,
    accessibilityLiveRegion,
    accessibilityElementsHidden,
    importantForAccessibility,
    accessibilityViewIsModal,
    accessibilityState,
    accessible: _accessible,
    ...rest
  } = props;

  const aria: AriaProps = {};

  if (nativeID !== undefined) {
    aria.id = nativeID;
  }

  if (accessibilityLabel !== undefined) {
    aria['aria-label'] = accessibilityLabel;
  }

  if (accessibilityLabelledBy !== undefined) {
    aria['aria-labelledby'] = accessibilityLabelledBy;
  }

  if (accessibilityRole !== undefined) {
    aria.role = accessibilityRole;
  }

  if (accessibilityHint !== undefined) {
    aria.title = accessibilityHint;
  }

  if (accessibilityLiveRegion !== undefined) {
    aria['aria-live'] = accessibilityLiveRegion === 'none' ? 'off' : accessibilityLiveRegion;
  }

  /**
   * Either platform's hide flag maps to the same `aria-hidden`. Only an explicit
   * hide sets it, so an `importantForAccessibility` of "auto" or "yes" is
   * consumed without contradicting an `accessibilityElementsHidden` beside it.
   */
  const hidden =
    accessibilityElementsHidden === true ||
    importantForAccessibility === 'no' ||
    importantForAccessibility === 'no-hide-descendants';

  if (hidden) {
    aria['aria-hidden'] = true;
  } else if (accessibilityElementsHidden === false) {
    aria['aria-hidden'] = false;
  }

  if (accessibilityViewIsModal !== undefined) {
    aria['aria-modal'] = accessibilityViewIsModal;
  }

  if (accessibilityState) {
    if (accessibilityState.disabled !== undefined) {
      aria['aria-disabled'] = accessibilityState.disabled;
    }

    if (accessibilityState.selected !== undefined) {
      aria['aria-selected'] = accessibilityState.selected;
    }

    if (accessibilityState.checked !== undefined) {
      aria['aria-checked'] = accessibilityState.checked;
    }

    if (accessibilityState.expanded !== undefined) {
      aria['aria-expanded'] = accessibilityState.expanded;
    }

    if (accessibilityState.busy !== undefined) {
      aria['aria-busy'] = accessibilityState.busy;
    }
  }

  return { rest, aria };
}
